// src/hooks/order/useOrderForm.ts
// Hook para manejo del formulario de pedidos
// Basado en useCotizacionForm pero adaptado para OrderForm

import { useState, useCallback, useEffect, useRef } from 'react'
import type { OrderData, Departamento, Ciudad } from '../../types/orderFormTypes'
import { shouldClearSingleOrderTransactionIdWhenPaymentMethodChanges } from '../../utils/orderPaymentTransactionRules'
import { ROLE_ID_ADMIN } from '../../constants/roleIds'
import { parseClienteNetoFlag } from '../../utils/clienteNetoFlag'
import { mergeRetencionFromCouponLines } from '../../utils/orderCouponLines'

/** Si el API trae retención / flag activa en meta_data[], copiarlo a extraDetails */
function mergeRetencionFromMeta(initialData: OrderData): OrderData {
  const prev = initialData.extraDetails || {}
  const extra = { ...prev }
  const md = initialData.meta_data as unknown
  const metaArr = Array.isArray(md) ? md : []

  const activaEntry = metaArr.find((m: { key?: string; value?: unknown }) => m.key === 'retencion_fuente_activa')
  const hasActivaMeta = activaEntry != null && activaEntry.value !== undefined && activaEntry.value !== null
  if (hasActivaMeta) {
    const v = String(activaEntry.value).trim().toLowerCase()
    extra.retencion_fuente_activa = v === '1' || v === 'true'
  }

  const retEntry = metaArr.find((m: { key?: string; value?: unknown }) => m.key === 'retencion_fuente')
  if (retEntry != null && retEntry.value != null && String(retEntry.value).trim() !== '') {
    const hadEmpty = !prev.retencion_fuente || String(prev.retencion_fuente).trim() === ''
    if (hadEmpty) extra.retencion_fuente = String(retEntry.value)
    if (!hasActivaMeta) extra.retencion_fuente_activa = true
  }

  if (prev.retencion_fuente != null && String(prev.retencion_fuente).trim() !== '' && extra.retencion_fuente_activa === undefined) {
    extra.retencion_fuente_activa = true
  }

  const changed =
    extra.retencion_fuente !== prev.retencion_fuente ||
    extra.retencion_fuente_activa !== prev.retencion_fuente_activa
  return changed ? { ...initialData, extraDetails: extra } : initialData
}

function mergeRetencionFromCouponLinesOnOrder(initialData: OrderData): OrderData {
  const merged = mergeRetencionFromCouponLines(initialData)
  if (!merged?.extraDetails) return initialData
  return { ...initialData, extraDetails: merged.extraDetails }
}

function mergeClienteNetoFromMeta(initialData: OrderData): OrderData {
  const prev = initialData.extraDetails || {}
  const md = initialData.meta_data as unknown
  const metaArr = Array.isArray(md) ? md : []
  const entry = metaArr.find((m: { key?: string; value?: unknown }) => m.key === 'cliente_neto')
  if (entry == null || entry.value === undefined || entry.value === null) {
    return initialData
  }
  const clienteNeto = parseClienteNetoFlag(entry.value)
  if (prev.cliente_neto === clienteNeto) return initialData
  return {
    ...initialData,
    extraDetails: { ...prev, cliente_neto: clienteNeto }
  }
}

function normalizeClienteNetoInExtraDetails(initialData: OrderData): OrderData {
  const prev = initialData.extraDetails || {}
  const raw = (prev as { cliente_neto?: unknown }).cliente_neto
  if (raw === undefined || raw === null) return initialData

  const clienteNeto = parseClienteNetoFlag(raw)
  if (prev.cliente_neto === clienteNeto) return initialData
  return {
    ...initialData,
    extraDetails: { ...prev, cliente_neto: clienteNeto }
  }
}

interface UseOrderFormProps {
  initialData?: OrderData
  mode: 'view' | 'edit' | 'create'
  departamentos: Departamento[]
  ciudades: Ciudad[]
  onFormChange?: (data: OrderData) => void
}

export function useOrderForm({
  initialData,
  mode,
  departamentos: deptos,
  ciudades: cities,
  onFormChange
}: UseOrderFormProps) {
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  const [formData, setFormData] = useState<OrderData>(() => {
    if (initialData) {
      const base = normalizeClienteNetoInExtraDetails(
        mergeClienteNetoFromMeta(
          mergeRetencionFromCouponLinesOnOrder(
            mergeRetencionFromMeta(initialData as OrderData)
          )
        )
      )
      // Si hay initialData y es modo create con role_id !== 1
      if (mode === 'create' && user.role_id !== ROLE_ID_ADMIN) {
        return { 
          ...base, 
          status: 'processing',
          date_created: new Date().toISOString(),
          date_modified: new Date().toISOString()
        } as OrderData
      }
      
      // Si hay initialData y es modo create normal
      if (mode === 'create') {
        return { 
          ...base, 
          date_created: new Date().toISOString(),
          date_modified: new Date().toISOString()
        } as OrderData
      }
      
      // Si hay initialData y es modo edit/view
      return base as OrderData
    }
    
    // Si no hay initialData, retornar objeto vacío
    return {} as OrderData
  })

  // Función para manejar cambios en los campos del formulario
  const handleInputChange = useCallback((
    section: keyof OrderData,
    field: string,
    value: any
  ) => {
    setFormData(prev => {
      const newData = { ...prev }
      
      // Manejar campos de nivel superior
      if (section === 'status' || section === 'guia' || section === 'agente' || section === 'total' || section === 'store_name') {
        newData[section] = value
      } else {
        // Manejar campos anidados
        if (!newData[section]) {
          newData[section] = {} as any
        }

        // Lógica especial para departamentos y ciudades
        // OrderForm guarda directamente los códigos (no nombres)
        if (
          (field === 'state' || field === 'city') &&
          (section === 'billingAddress' || section === 'shippingAddress')
        ) {
          (newData[section] as any)[field] = value
          if (field === 'state') {
            (newData[section] as any)['city'] = ''
          }
        } else {
          (newData[section] as any)[field] = value
        }
      }

      // Sincronizar forma_pago cuando se cambia payment_method (inmediatamente, no en useEffect)
      // Esto evita problemas de timing cuando se valida el formulario
      if (section === 'extraDetails' && field === 'payment_method' && value) {
        if (!newData.extraDetails) {
          newData.extraDetails = {} as any
        }
        
        let formaPagoValue = '6' // Por defecto contado
        const pm = String(value).trim().toLowerCase()
        
        if (pm === 'cod' || pm === 'cmdm' || pm === 'addi') {
          formaPagoValue = '4' // Crédito/Contra entrega
        } else if (pm === 'bold' || pm === 'dataf') {
          formaPagoValue = '6' // Contado
        }
        // Para 'part' se maneja en useOrderPaymentSync porque necesita pagosParciales
        
        // Actualizar forma_pago inmediatamente
        (newData.extraDetails as any).forma_pago = formaPagoValue

        // Evitar enviar ID de transacción de un método anterior (p. ej. Wompi → MDM / efectivo / Bold → Wompi)
        const prevPm = prev.extraDetails?.payment_method
        if (shouldClearSingleOrderTransactionIdWhenPaymentMethodChanges(prevPm, value)) {
          (newData.extraDetails as any).transaction_id = ''
        }
      }
      
      return newData
    })
  }, [])

  // Ref para evitar llamadas durante la inicialización
  const isInitialMount = useRef(true)
  const prevFormDataRef = useRef<OrderData>(formData)

  // Efecto para notificar cambios al componente padre (después del render)
  useEffect(() => {
    // Evitar llamada en el primer render
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevFormDataRef.current = formData
      return
    }

    // Solo notificar si realmente cambió el formData
    if (onFormChange && JSON.stringify(prevFormDataRef.current) !== JSON.stringify(formData)) {
      prevFormDataRef.current = formData
      // Usar setTimeout para asegurar que se ejecute después del render
      setTimeout(() => {
        onFormChange(formData)
      }, 0)
    }
  }, [formData, onFormChange])

  // Efecto para actualizar el destino del pedido cuando cambia la ciudad de envío
  useEffect(() => {
    if (formData.shippingAddress?.city) {
      // Importar función de utilidades
      const esCiudadMetropolitana = (ciudad: string): boolean => {
        if (!ciudad) return false
        const codigosMetropolitanos = ['05001', '05088', '05266', '05360', '05631']
        return codigosMetropolitanos.includes(ciudad)
      }
      
      const esMetropolitana = esCiudadMetropolitana(formData.shippingAddress.city)
      const nuevoDestino = esMetropolitana ? 'metropolitana' : 'nacional'
      
      if (formData.extraDetails?.destino_pedido !== nuevoDestino) {
        handleInputChange('extraDetails', 'destino_pedido', nuevoDestino)
      }
    }
  }, [formData.shippingAddress?.city, formData.extraDetails?.destino_pedido, handleInputChange])

  // Efecto para establecer el agente al cargar el formulario
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    if (user.id && !formData.agente) {
      handleInputChange('agente', '', user.id)
    }
    // En modo edit, si el agente está vacío, asignar ID 0 por defecto
    if (mode === 'edit' && !formData.agente) {
      handleInputChange('agente', '', 0)
    }
  }, [formData.agente, handleInputChange, mode])

  return {
    formData,
    setFormData,
    handleInputChange
  }
}

